/**
 * AetherFifaContext — IDataService adapter over Aether GraphQL.
 *
 * getContext() returns the raw FIFA fixture RECORD for the bound
 * fixtureId. The record is pulled from `allUpcomingFixtures` (the
 * same query the catalogue uses) and filtered to the picked fixture —
 * it carries the identity data captions bind against: team ids,
 * names, tricodes, status, date, venue, scores.
 *
 * Why not `fixtureMetricChannel`: that query is the AeCheck
 * live-metrics feed. It only populates while AeCheck is actively
 * running for a fixture, and returns just `fixtureName` otherwise —
 * no use for the identity layer. When live FIFA stats are needed,
 * fixtureMetricChannel comes back as a SECOND fetch merged in here;
 * see the deferred note in fifaToDdsShape.js.
 *
 * Pure transport — returns the FIFA record as-is. Reshaping into the
 * DDS-PLS shape is fifaToDdsShape's job, composed via withContextShape.
 *
 * Caching: one fetch per adapter instance; in-flight coalescing; a
 * failed fetch does not poison the cache (next call retries). Same
 * contract as DdsPlsContext.
 *
 * Lifecycle: bound to a single fixtureId; useFixtureContext builds a
 * fresh adapter when the fixture changes.
 */
import { gql } from "@apollo/client";

const ALL_UPCOMING_FIXTURES = gql`
  query AllUpcomingFixtures($competitionIds: [String!], $databaseName: String) {
    allUpcomingFixtures(
      competitionIds: $competitionIds,
      databaseName: [$databaseName]
    )
  }
`;

const EMPTY = Object.freeze({});

export function createAetherFifaContext({ client, fixtureId, competitionId, databaseName }) {
  if (!client) throw new Error("AetherFifaContext: `client` is required");
  if (!databaseName) throw new Error("AetherFifaContext: `databaseName` is required");

  let cached = null;        // null until first successful fetch
  let inFlight = null;      // Promise during an in-flight fetch
  const listeners = new Set();

  return {
    /** @returns {Promise<object>} the raw FIFA fixture record, or {} */
    async getContext() {
      if (!fixtureId) return EMPTY;
      if (cached) return cached;
      if (inFlight) return inFlight;

      const variables = { databaseName };
      // Filter the query to the picked competition when known; the
      // fixture is still located by id against the response, so an
      // absent competitionId just means a wider query.
      if (competitionId) variables.competitionIds = [competitionId];

      inFlight = client.query({ query: ALL_UPCOMING_FIXTURES, variables })
        .then((response) => {
          const competitionDocs = response?.data?.allUpcomingFixtures ?? [];
          let found = null;
          for (const doc of competitionDocs) {
            for (const fx of doc?.fixtures ?? []) {
              if (fx?.aetFixtureId === fixtureId) { found = fx; break; }
            }
            if (found) break;
          }
          cached = found || EMPTY;
          inFlight = null;
          return cached;
        })
        .catch((err) => {
          inFlight = null;
          throw err;
        });
      return inFlight;
    },

    /**
     * @param {() => void} listener
     * @returns {() => void} unsubscribe
     */
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    /** Diagnostics hook — surfaces basic transport state. */
    getState() {
      return {
        fixtureId: fixtureId || null,
        competitionId: competitionId || null,
        databaseName,
        hasCache: !!cached,
        inFlight: !!inFlight,
        listeners: listeners.size,
      };
    },
  };
}
